import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Users, Key, Loader2, Copy, Check, User, Mail, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTradies = () => {
  const navigate = useNavigate();
  const [tradies, setTradies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedTradie, setSelectedTradie] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [customPassword, setCustomPassword] = useState('');
  const [useCustomPassword, setUseCustomPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchTradies();
  }, []);

  const fetchTradies = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/tradies`);
      setTradies(response.data);
    } catch (error) {
      toast.error('Failed to load tradies');
    } finally {
      setLoading(false);
    }
  };

  const openResetDialog = (tradie) => {
    setSelectedTradie(tradie);
    setNewPassword('');
    setCustomPassword('');
    setUseCustomPassword(false);
    setCopied(false);
    setResetDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!selectedTradie) return;
    
    setResetting(true);
    try {
      const payload = useCustomPassword && customPassword 
        ? { new_password: customPassword } 
        : {};
      
      const response = await axios.post(
        `${API_URL}/admin/tradies/${selectedTradie.id}/reset-password`,
        payload
      );
      
      setNewPassword(response.data.temporary_password);
      toast.success(`Password reset for ${selectedTradie.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
      setResetDialogOpen(false);
    } finally {
      setResetting(false);
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(newPassword);
      setCopied(true);
      toast.success('Password copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy password');
    }
  };

  const closeDialog = () => {
    setResetDialogOpen(false);
    setSelectedTradie(null);
    setNewPassword('');
  };

  return (
    <div className="min-h-screen bg-slate-100 page-enter">
      {/* Header */}
      <header className="bg-[#1F2A37] text-white p-4 shadow-lg sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            data-testid="back-btn"
            onClick={() => navigate('/admin')}
            className="text-white hover:bg-slate-700"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight">Manage Tradies</h1>
            <p className="text-sm text-slate-300">Reset passwords & view accounts</p>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="p-4 max-w-4xl mx-auto">
        <Card className="border-2 border-slate-200 shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-500" />
              Registered Tradies ({tradies.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
              </div>
            ) : tradies.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No tradies registered yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Joined</TableHead>
                      <TableHead className="font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tradies.map((tradie) => (
                      <TableRow key={tradie.id} data-testid={`tradie-row-${tradie.id}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-orange-600" />
                            </div>
                            {tradie.name}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">
                          <div className="flex items-center gap-1">
                            <Mail className="w-4 h-4 text-slate-400" />
                            {tradie.email}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {format(new Date(tradie.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`reset-password-btn-${tradie.id}`}
                            onClick={() => openResetDialog(tradie)}
                            className="text-orange-600 border-orange-200 hover:bg-orange-50 hover:border-orange-300"
                          >
                            <Key className="w-4 h-4 mr-1" />
                            Reset Password
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-orange-500" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              {selectedTradie && (
                <>Reset password for <strong>{selectedTradie.name}</strong> ({selectedTradie.email})</>
              )}
            </DialogDescription>
          </DialogHeader>

          {!newPassword ? (
            // Before reset
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="useCustom"
                  checked={useCustomPassword}
                  onChange={(e) => setUseCustomPassword(e.target.checked)}
                  className="w-4 h-4 text-orange-500"
                />
                <Label htmlFor="useCustom" className="text-sm cursor-pointer">
                  Set custom password (optional)
                </Label>
              </div>

              {useCustomPassword && (
                <div className="space-y-2">
                  <Label htmlFor="customPassword">New Password</Label>
                  <Input
                    id="customPassword"
                    data-testid="custom-password-input"
                    type="text"
                    placeholder="Enter new password"
                    value={customPassword}
                    onChange={(e) => setCustomPassword(e.target.value)}
                    className="h-12"
                  />
                </div>
              )}

              <p className="text-sm text-slate-500">
                {useCustomPassword 
                  ? "The tradie will use this password to log in."
                  : "A temporary password will be generated automatically."}
              </p>
            </div>
          ) : (
            // After reset - show new password
            <div className="py-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-800 font-medium mb-2">Password Reset Successful!</p>
                <p className="text-sm text-green-700">Share this password with the tradie:</p>
              </div>

              <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-3">
                <code className="flex-1 text-lg font-mono font-bold text-[#1F2A37]">
                  {newPassword}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="copy-password-btn"
                  onClick={copyPassword}
                  className={copied ? "bg-green-100 border-green-300" : ""}
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>

              <p className="text-sm text-amber-600 mt-3">
                ⚠️ This password won't be shown again. Make sure to copy it now.
              </p>
            </div>
          )}

          <DialogFooter>
            {!newPassword ? (
              <>
                <Button variant="outline" onClick={closeDialog}>
                  Cancel
                </Button>
                <Button
                  data-testid="confirm-reset-btn"
                  onClick={handleResetPassword}
                  disabled={resetting || (useCustomPassword && !customPassword)}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {resetting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Key className="w-4 h-4 mr-2" />
                  )}
                  Reset Password
                </Button>
              </>
            ) : (
              <Button
                data-testid="done-btn"
                onClick={closeDialog}
                className="bg-orange-500 hover:bg-orange-600"
              >
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTradies;
